/**
 * InspectRadarGrid — Grok-style room × trade grid, per-room-type columns.
 *
 * Each room renders ONLY the trades that make sense for its room type
 * (bedroom = walls/floor/ceiling/windows/doors/closet/electrical;
 *  bathroom = walls/floor/vanity/tub_shower/toilet/plumbing; etc.).
 *
 * The grid header shows the UNION of all trades across the rooms shown,
 * so bedrooms and bathrooms can live in the same table. Cells that don't
 * apply to a room's type render as "—" and are disabled.
 *
 * Admins tune the templates per-tenant at /admin/settings/inspection-templates.
 * The templates + classifier come from GET /api/settings/inspection-trade-templates.
 *
 * On photo upload, we hit /api/inspection-autotag — Claude Vision suggests
 * the trade + condition. Confidence ≥ 0.75 → auto-apply the rating.
 * Lower confidence → show a one-tap "Accept suggestion" toast.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle, Flag, Sparkle } from "@phosphor-icons/react";

// Fallback catalog used before the templates fetch returns. Keeps the
// Grid renderable in offline/first-paint cases.
const FALLBACK_CATALOG = {
  overall:     { label: "Room",       short: "OVL" },
  walls:       { label: "Walls",      short: "WAL" },
  floor:       { label: "Floor",      short: "FLR" },
  ceiling:     { label: "Ceiling",    short: "CLG" },
  windows:     { label: "Windows",    short: "WIN" },
  doors:       { label: "Doors",      short: "DRS" },
  fixtures:    { label: "Fixtures",   short: "FIX" },
  plumbing:    { label: "Plumbing",   short: "PLB" },
  electrical:  { label: "Electrical", short: "ELC" },
  closet:      { label: "Closet",     short: "CLS" },
  vanity:      { label: "Vanity",     short: "VAN" },
  tub_shower:  { label: "Tub/Shower", short: "TUB" },
  toilet:      { label: "Toilet",     short: "TLT" },
  cabinets:    { label: "Cabinets",   short: "CAB" },
  countertops: { label: "Counters",   short: "CTR" },
  appliances:  { label: "Appliances", short: "APP" },
  vents:       { label: "Vents",      short: "VNT" },
  shelving:    { label: "Shelving",   short: "SHV" },
};

const FALLBACK_TEMPLATES = {
  bedroom:    ["overall", "walls", "floor", "ceiling", "windows", "doors", "closet", "electrical"],
  bathroom:   ["overall", "walls", "floor", "ceiling", "vanity", "tub_shower", "toilet", "plumbing"],
  half_bath:  ["overall", "walls", "floor", "ceiling", "vanity", "toilet", "plumbing"],
  kitchen:    ["overall", "walls", "floor", "cabinets", "countertops", "appliances", "plumbing", "fixtures"],
  living_room:["overall", "walls", "floor", "ceiling", "windows", "doors", "fixtures", "electrical"],
  dining:     ["overall", "walls", "floor", "ceiling", "windows", "doors", "fixtures", "electrical"],
  hallway:    ["overall", "walls", "floor", "ceiling", "doors", "fixtures", "electrical"],
  closet:     ["overall", "walls", "floor", "ceiling", "doors", "shelving", "electrical"],
  laundry:    ["overall", "walls", "floor", "appliances", "plumbing", "electrical", "vents"],
  garage:     ["overall", "walls", "floor", "doors", "electrical", "plumbing"],
  basement:   ["overall", "walls", "floor", "ceiling", "electrical", "plumbing", "vents"],
  office:     ["overall", "walls", "floor", "ceiling", "windows", "doors", "electrical"],
  patio:      ["overall", "floor", "doors", "fixtures", "electrical"],
  other:      ["overall", "walls", "floor", "ceiling", "windows", "doors", "fixtures", "electrical", "plumbing"],
};

// Keep this classifier in sync with routes/inspection_templates.py:classify_room.
// Order matters — more-specific first.
const ROOM_RULES = [
  [/\bhalf\s*bath|powder\s*room|half-bath/i, "half_bath"],
  [/\b(master\s*)?bath(room)?\b/i,           "bathroom"],
  [/\bbed(room)?\b|\bbr\d*\b|\bbdrm/i,       "bedroom"],
  [/\bkitchen|kitch\b/i,                     "kitchen"],
  [/\bliving|family\s*room|great\s*room|\bden\b|\blounge/i, "living_room"],
  [/\bdining|dinette/i,                      "dining"],
  [/\blaundry|utility|mudroom|wash\b/i,      "laundry"],
  [/\bgarage/i,                              "garage"],
  [/\bbasement|cellar/i,                     "basement"],
  [/\boffice|study/i,                        "office"],
  [/\bhall(way)?|foyer|entry|entrance|stairwell|landing/i, "hallway"],
  [/\bcloset|pantry|wardrobe/i,              "closet"],
  [/\bpatio|porch|deck|balcony|yard/i,       "patio"],
];
const classifyRoom = (name) => {
  const n = (name || "").trim();
  if (!n) return "other";
  for (const [re, rt] of ROOM_RULES) if (re.test(n)) return rt;
  return "other";
};

const CONDS = [
  { key: "great",  label: "Great",  tone: "bg-emerald-500 text-white", short: "G" },
  { key: "good",   label: "Good",   tone: "bg-lime-500 text-white",    short: "✓" },
  { key: "fair",   label: "Fair",   tone: "bg-amber-500 text-white",   short: "~" },
  { key: "poor",   label: "Poor",   tone: "bg-orange-500 text-white",  short: "!" },
  { key: "broken", label: "Broken", tone: "bg-red-600 text-white",     short: "X" },
];
const CELL_TONE = {
  great:  "bg-emerald-500 border-emerald-600 text-white",
  good:   "bg-lime-500 border-lime-600 text-white",
  fair:   "bg-amber-500 border-amber-600 text-white",
  poor:   "bg-orange-500 border-orange-600 text-white",
  broken: "bg-red-600 border-red-700 text-white",
  na:     "bg-slate-300 border-slate-400 text-slate-700",
  empty:  "bg-white border-slate-300 text-slate-400",
  disabled: "bg-slate-50 border-slate-200 text-slate-300",
};

function getCellValue(room, trade) {
  if (trade === "overall") return room?.condition || "";
  return (room?.sub_checks || {})[`${trade}_condition`] || "";
}
function countRoomPhotos(room, trade) {
  const bucket = room?._photos || [];
  if (trade === "overall") return bucket.length;
  return bucket.filter(p => p.inspection_trade === trade).length;
}

export default function InspectRadarGrid({ jobId, phase, rooms, onRoomsChanged, isSaving }) {
  const [openCell, setOpenCell] = useState(null);
  const [notes, setNotes] = useState("");
  const [autoFlag, setAutoFlag] = useState(false);
  const [savingCell, setSavingCell] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [autotagging, setAutotagging] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { trade, condition, confidence, label }

  // Load tenant-level templates
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings/inspection-trade-templates");
        if (cancelled) return;
        if (r.data?.catalog)   setCatalog(r.data.catalog);
        if (r.data?.templates) setTemplates(r.data.templates);
      } catch (e) {
        // keep fallbacks
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the per-row "applicable trades" and the union set for the header.
  const roomTrades = useMemo(
    () => rooms.map(r => {
      const rt = classifyRoom(r.room);
      const trades = (templates[rt] || templates.other || FALLBACK_TEMPLATES.other).filter(t => catalog[t]);
      return { room: r.room, room_type: rt, trades: new Set(trades) };
    }),
    [rooms, templates, catalog],
  );
  const headerCols = useMemo(() => {
    // Union + stable order: overall first, then by first-appearance across rows.
    const seen = new Set(); const out = [];
    const push = (k) => { if (!seen.has(k) && catalog[k]) { seen.add(k); out.push(k); } };
    push("overall");
    for (const row of roomTrades) for (const t of row.trades) push(t);
    return out;
  }, [roomTrades, catalog]);

  const stats = useMemo(() => {
    let rated = 0, total = 0, needsAttention = 0;
    rooms.forEach((r, i) => {
      const applicable = roomTrades[i]?.trades || new Set();
      for (const col of headerCols) {
        if (!applicable.has(col)) continue;
        total += 1;
        const v = getCellValue(r, col);
        if (v && v !== "") rated += 1;
        if (["fair", "poor", "broken"].includes(v)) needsAttention += 1;
      }
    });
    return { rated, total, needsAttention, pct: total ? Math.round(rated * 100 / total) : 0 };
  }, [rooms, roomTrades, headerCols]);

  const openSheet = (roomName, trade) => {
    const room = rooms.find(r => r.room === roomName);
    let n = "";
    if (room) {
      if (trade === "overall") n = room.notes || "";
      else n = (room.sub_checks || {})[`${trade}_notes`] || "";
    }
    const existing = room ? getCellValue(room, trade) : "";
    setOpenCell({ roomName, trade });
    setNotes(n);
    setAutoFlag(["fair", "poor", "broken"].includes(existing));
    setSuggestion(null);
  };
  const closeSheet = () => {
    setOpenCell(null); setNotes(""); setAutoFlag(false); setSuggestion(null);
  };

  const current = useMemo(() => {
    if (!openCell) return null;
    return rooms.find(r => r.room === openCell.roomName) || null;
  }, [openCell, rooms]);
  const currentValue = current && openCell ? getCellValue(current, openCell.trade) : "";

  // Apply a rating value (overall or trade) + optional notes
  const applyRating = async ({ roomName, trade, rating, newNotes }) => {
    const params = new URLSearchParams({ room: roomName });
    let patch = {};
    if (trade === "overall") {
      patch = { condition: rating };
      if (newNotes != null && newNotes.trim()) patch.notes = newNotes.trim();
    } else {
      patch = { sub_checks: { [`${trade}_condition`]: rating } };
      if (newNotes != null && newNotes.trim()) patch.sub_checks[`${trade}_notes`] = newNotes.trim();
    }
    await api.patch(`/jobs/${jobId}/inspection/${phase}/room?${params.toString()}`, patch);
  };

  const saveRating = async (rating) => {
    if (!openCell || !current) return;
    setSavingCell(true);
    try {
      await applyRating({ roomName: openCell.roomName, trade: openCell.trade, rating, newNotes: notes });
      if (["fair", "poor", "broken"].includes(rating)) setAutoFlag(true); else setAutoFlag(false);
      onRoomsChanged && onRoomsChanged();
      toast.success(`${openCell.roomName} · ${openCell.trade === "overall" ? "Overall" : openCell.trade}: ${rating}`,
                     { duration: 1200 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSavingCell(false); }
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("room", openCell.roomName);
    if (openCell.trade !== "overall") fd.append("trade", openCell.trade);
    if (autoFlag) fd.append("auto_flag", "true");
    const up = await api.post(`/jobs/${jobId}/inspection/${phase}/room/photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // Response doesn't include file path directly in all versions; find
    // it on the latest uploaded entry via a lightweight re-read is costly,
    // so grab it from the response if available.
    return up?.data?.photo?.path || up?.data?.path || "";
  };

  const requestAutotag = async (filePath, roomName) => {
    if (!filePath) return null;
    try {
      const r = await api.post("/inspection-autotag", {
        file_path: filePath,
        room_name: roomName,
      });
      return r.data || null;
    } catch (e) {
      return null;
    }
  };

  const burstPhotos = async (fileList) => {
    if (!openCell || !fileList || fileList.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    let firstPath = "";
    for (const file of Array.from(fileList).slice(0, 20)) {
      try {
        const p = await uploadOne(file);
        if (!firstPath && p) firstPath = p;
        ok += 1;
      } catch { fail += 1; }
    }
    setUploading(false);
    if (ok > 0) toast.success(`Captured ${ok} photo${ok === 1 ? "" : "s"}${autoFlag ? " · auto-flagged" : ""}`);
    if (fail > 0) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed`);
    onRoomsChanged && onRoomsChanged();

    // Fire Claude Vision autotag on the FIRST uploaded photo
    if (firstPath && openCell) {
      setAutotagging(true);
      const tag = await requestAutotag(firstPath, openCell.roomName);
      setAutotagging(false);
      if (!tag || (!tag.auto_apply && !tag.suggest)) return;
      if (tag.auto_apply) {
        // Auto-apply the suggested rating to the suggested trade cell
        try {
          await applyRating({ roomName: openCell.roomName, trade: tag.trade, rating: tag.condition });
          onRoomsChanged && onRoomsChanged();
          toast.success(<SuggestionToast tag={tag} applied/>, { duration: 3500 });
        } catch (e) {
          setSuggestion(tag); // fall back to manual accept
        }
      } else {
        setSuggestion(tag);
      }
    }
  };

  const acceptSuggestion = async () => {
    if (!suggestion || !openCell) return;
    try {
      await applyRating({
        roomName: openCell.roomName,
        trade: suggestion.trade,
        rating: suggestion.condition,
      });
      onRoomsChanged && onRoomsChanged();
      toast.success(`Applied: ${suggestion.trade_label || suggestion.trade} · ${suggestion.condition}`);
      setSuggestion(null);
    } catch (e) {
      toast.error("Could not apply suggestion");
    }
  };

  return (
    <div className="relative" data-testid="inspect-radar-grid">
      {/* Progress bar */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[#0f172a] px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">
            Grid · {stats.rated}/{stats.total} rated · {stats.pct}%
          </div>
          {stats.needsAttention > 0 && (
            <div className="text-[10px] font-black uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 rounded-full"
                 data-testid="grid-attention-count">
              {stats.needsAttention} needs work
            </div>
          )}
        </div>
        <div className="mt-1 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${stats.pct}%` }}/>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs" data-testid="grid-table">
          <thead>
            <tr className="bg-slate-50">
              <th className="sticky left-0 z-[1] bg-slate-50 text-left px-2 py-2 border-b-2 border-slate-200 min-w-[120px]">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Room</span>
              </th>
              {headerCols.map(col => {
                const meta = catalog[col] || { label: col, short: col.slice(0, 3).toUpperCase() };
                return (
                  <th key={col} className="px-1 py-2 border-b-2 border-slate-200 text-center min-w-[52px]">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">{meta.short}</div>
                    <div className="text-[9px] text-slate-400 normal-case font-medium">{meta.label}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rooms.map((r, ri) => {
              const applicable = roomTrades[ri]?.trades || new Set();
              return (
                <tr key={r.room} className={ri % 2 ? "bg-slate-50/50" : "bg-white"}>
                  <td className="sticky left-0 bg-inherit px-2 py-1 border-b border-slate-100 font-bold text-[#0B1F3A] text-xs truncate"
                      data-testid={`grid-room-label-${r.room}`}
                      title={roomTrades[ri]?.room_type || "other"}>
                    {r.room}
                    <span className="text-[9px] text-slate-400 ml-1 font-normal normal-case">
                      ({roomTrades[ri]?.room_type || "other"})
                    </span>
                    {r.completed && <CheckCircle weight="fill" size={12} className="inline ml-1 text-emerald-500"/>}
                  </td>
                  {headerCols.map(col => {
                    const applies = applicable.has(col);
                    if (!applies) {
                      return (
                        <td key={col} className="px-0.5 py-1 border-b border-slate-100">
                          <div className={`w-full aspect-square max-w-[48px] mx-auto flex items-center justify-center rounded border-2 font-black text-sm ${CELL_TONE.disabled}`}
                            data-testid={`grid-cell-disabled-${r.room}-${col}`}
                            title={`Not applicable to ${roomTrades[ri]?.room_type || "this room"}`}>—</div>
                        </td>
                      );
                    }
                    const v = getCellValue(r, col);
                    const tone = v ? CELL_TONE[v] : CELL_TONE.empty;
                    const label = v ? (CONDS.find(c => c.key === v)?.short || "·") : "+";
                    const pcount = countRoomPhotos(r, col);
                    return (
                      <td key={col} className="px-0.5 py-1 border-b border-slate-100">
                        <button type="button" onClick={() => openSheet(r.room, col)}
                          data-testid={`grid-cell-${r.room}-${col}`}
                          className={`relative w-full aspect-square max-w-[48px] mx-auto flex items-center justify-center rounded border-2 font-black text-sm transition-all hover:scale-105 ${tone}`}>
                          {label}
                          {pcount > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-[#0f172a] text-white text-[9px] font-black rounded-full flex items-center justify-center"
                              data-testid={`grid-cell-photos-${r.room}-${col}`}>
                              {pcount}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quick-rate sheet */}
      {openCell && current && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
             onClick={closeSheet} data-testid="grid-rate-sheet">
          <div className="w-full sm:max-w-md bg-white border-t-4 sm:border-4 border-[#0f172a] sm:rounded-lg sm:shadow-[6px_6px_0px_#0f172a]"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-3 border-b-2 border-slate-200">
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {openCell.trade === "overall" ? "Room overall" : (catalog[openCell.trade]?.label || openCell.trade)}
                </div>
                <div className="text-base font-bold text-[#0B1F3A] truncate" data-testid="sheet-title">
                  {openCell.roomName}
                </div>
                {currentValue && (
                  <div className="text-[11px] mt-0.5">
                    Current: <span className={`inline-block px-1.5 py-0.5 rounded font-black uppercase text-[9px] tracking-widest ${CELL_TONE[currentValue] || ""}`}>{currentValue}</span>
                  </div>
                )}
              </div>
              <button onClick={closeSheet} data-testid="sheet-close"
                className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
            </div>

            <div className="p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Rate</div>
              <div className="grid grid-cols-5 gap-1.5">
                {CONDS.map(c => (
                  <button key={c.key} onClick={() => saveRating(c.key)}
                    disabled={savingCell} data-testid={`sheet-rate-${c.key}`}
                    className={`h-14 rounded flex flex-col items-center justify-center font-black uppercase text-[10px] tracking-widest transition-all disabled:opacity-40 ${
                      currentValue === c.key ? `${c.tone} ring-4 ring-[#0f172a] scale-105` : `${c.tone} hover:scale-105`
                    }`}>
                    <span className="text-base leading-none">{c.short}</span>
                    <span className="mt-0.5">{c.label}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => saveRating("na")} disabled={savingCell} data-testid="sheet-rate-na"
                className={`mt-1.5 w-full h-8 rounded font-black uppercase text-[10px] tracking-widest ${
                  currentValue === "na" ? "bg-slate-500 text-white ring-2 ring-[#0f172a]" : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}>
                Not applicable
              </button>
            </div>

            {/* AI suggestion banner (shows when Claude's confidence is medium) */}
            {suggestion && (
              <div className="mx-3 mb-2 rounded-lg border-2 border-fuchsia-400 bg-fuchsia-50 p-2.5" data-testid="sheet-ai-suggestion">
                <div className="flex items-start gap-2">
                  <Sparkle weight="fill" size={16} className="text-fuchsia-600 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-900">
                      AI suggests · {Math.round((suggestion.confidence || 0) * 100)}% sure
                    </div>
                    <div className="text-xs font-bold text-fuchsia-950">
                      {suggestion.trade_label || suggestion.trade} · {suggestion.condition}
                    </div>
                    {suggestion.label && <div className="text-[11px] text-fuchsia-800 truncate italic">"{suggestion.label}"</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={acceptSuggestion}
                      data-testid="sheet-ai-accept"
                      className="h-7 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[10px] font-black uppercase tracking-widest rounded">
                      Accept
                    </button>
                    <button onClick={() => setSuggestion(null)}
                      data-testid="sheet-ai-dismiss"
                      className="h-7 px-2 bg-white hover:bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-widest rounded border border-slate-200">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="px-3 pb-3 space-y-2">
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" multiple
                  className="hidden" data-testid="sheet-file-input"
                  onChange={(e) => { burstPhotos(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}/>
                <button onClick={() => fileRef.current?.click()}
                  disabled={uploading || autotagging}
                  data-testid="sheet-capture-btn"
                  className="flex-1 h-11 bg-[#00C4CC] hover:bg-[#00a8af] disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest flex items-center justify-center gap-1.5">
                  {uploading ? <>Uploading…</> : autotagging ? <><Sparkle weight="fill" size={16}/>AI tagging…</> : <><Camera weight="fill" size={16}/>Capture photos</>}
                </button>
              </div>
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded border-2 border-amber-200 bg-amber-50">
                <input type="checkbox" checked={autoFlag} onChange={(e) => setAutoFlag(e.target.checked)}
                  data-testid="sheet-auto-flag" className="h-4 w-4 accent-amber-600"/>
                <Flag weight="bold" size={14} className="text-amber-700"/>
                <span className="text-xs font-bold text-amber-900 flex-1">Auto-flag next capture as tenant damage</span>
              </label>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} maxLength={500} data-testid="sheet-notes"
                  placeholder={openCell.trade === "overall" ? "Overall room notes…" : `${catalog[openCell.trade]?.label || openCell.trade} notes…`}
                  className="w-full p-2 border-2 border-slate-200 text-xs focus:outline-none focus:border-[#00C4CC] mt-1"/>
              </div>
              <button onClick={() => { if (!currentValue) { closeSheet(); return; } saveRating(currentValue); }}
                disabled={savingCell || !currentValue} data-testid="sheet-save-notes"
                className="w-full h-9 rounded bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
                {savingCell ? "Saving…" : "Save notes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionToast({ tag, applied }) {
  return (
    <div className="flex items-center gap-2">
      <Sparkle weight="fill" size={14} className="text-fuchsia-500"/>
      <div className="text-xs">
        <div className="font-black">AI auto-applied</div>
        <div className="text-slate-600">
          {tag.trade_label || tag.trade} · {tag.condition} · {Math.round((tag.confidence || 0) * 100)}%
        </div>
      </div>
    </div>
  );
}
